import { CompetitionSummary, EvaluationSummary, HostedCompetitionSummary } from '../../types/interfaces';
import { formatDate, localDayIndex } from '../../utils/dates';

export interface AccountData {
  joined: CompetitionSummary[] | null;
  judging: EvaluationSummary[] | null;
  hosting: HostedCompetitionSummary[] | null;
  unread: number | null;
}

export interface AttentionItem {
  key: string;
  text: string;
  to: string;
}

const MAX_ITEMS = 5;
const CLOSING_DAYS = 7;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const isOpen = (status?: string) => (status || '').toLowerCase() === 'open';
/** Judging runs while open and after submissions close; archiving ends it. */
const isJudgeable = (status?: string) => ['open', 'closed'].includes((status || '').toLowerCase());

export function attentionItems(data: AccountData, now: Date): AttentionItem[] {
  const items: AttentionItem[] = [];
  if (data.unread && data.unread > 0) {
    items.push({ key: 'unread', text: plural(data.unread, 'unread notification'), to: '/profile/notifications' });
  }

  (data.judging || [])
    .filter((e) => isJudgeable(e.status) && e.max_votes > e.current_votes)
    .sort((a, b) => (b.max_votes - b.current_votes) - (a.max_votes - a.current_votes))
    .forEach((e) => items.push({
      key: `judging-${e.competition_id}`,
      text: `${e.title}: ${plural(e.max_votes - e.current_votes, 'evaluation')} left`,
      to: `/evaluations/${e.competition_id}`,
    }));

  const today = localDayIndex(now) as number;
  (data.joined || [])
    .map((c) => ({ c, day: localDayIndex(c.end_date) }))
    .filter(({ c, day }) => isOpen(c.status) && day !== null && day >= today && day - today <= CLOSING_DAYS)
    .sort((a, b) => (a.day as number) - (b.day as number))
    .forEach(({ c }) => items.push({
      key: `closing-${c.competition_id}`,
      text: `${c.title} closes ${formatDate(c.end_date)}`,
      to: `/competition/${c.competition_id}`,
    }));

  return items.slice(0, MAX_ITEMS);
}

export function totalCompetitions(data: AccountData): number | null {
  const lengths = [data.joined, data.judging, data.hosting]
    .filter((l) => Array.isArray(l))
    .map((l) => (l as unknown[]).length);
  return lengths.length ? lengths.reduce((sum, n) => sum + n, 0) : null;
}
